import { useState, useRef, useEffect } from "react";
import {
    calculateAnnotationPoint,
    calculateZoomTransform,
} from "../../../features/consultation-observations/documents/document.utils";
import { toast } from "react-toastify";
import axios from "axios";
import { API_BASE_URL } from "../../../app_url";

/**
 * ImagePreviewModal - Standalone image preview component with annotations
 * Features: zoom, pan, add/edit/delete annotations
 */
const ImagePreviewModal = ({
    imageUrl,
    onClose,
    doctorId,
    readOnly = false,
}) => {
    // ============================================
    // STATE MANAGEMENT
    // ============================================
    
    // Image annotation state
    const [annotations, setAnnotations] = useState([]);
    const [draft, setDraft] = useState(null);
    const [edit, setEdit] = useState(null);
    
    // Image zoom/pan state
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [baseSize, setBaseSize] = useState({ w: 0, h: 0 });

    // Refs
    const viewportRef = useRef(null);
    const imgRef = useRef(null);

    // ============================================
    // LOAD ANNOTATIONS
    // ============================================

    useEffect(() => {
        if (!imageUrl || !doctorId) return;

        const loadAnnotations = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}doctor-documents/annotations`, {
                    params: { image_url: imageUrl, doctor_id: doctorId },
                });
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setAnnotations(res.data.data);
                } else {
                    setAnnotations([]);
                }
            } catch (err) {
                console.error("Load annotations error:", err);
                setAnnotations([]);
            }
        };

        loadAnnotations();
    }, [imageUrl, doctorId]);

    // ============================================
    // IMAGE ZOOM & PAN
    // ============================================

    const handleImgLoad = (e) => {
        const img = e.target;
        setBaseSize({ w: img.naturalWidth, h: img.naturalHeight });
    };

    const zoomIn = () => setScale((s) => Math.min(s + 0.25, 5));
    const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));
    const resetView = () => {
        setScale(1);
        setTx(0);
        setTy(0);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const viewport = viewportRef.current;
        if (!viewport) return;
        const rect = viewport.getBoundingClientRect();
        const next = calculateZoomTransform({
            cursorX: e.clientX - rect.left,
            cursorY: e.clientY - rect.top,
            scale,
            translateX: tx,
            translateY: ty,
            deltaY: e.deltaY,
            minScale: 0.5,
            maxScale: 5,
            step: 0.1,
        });
        setScale(next.scale);
        setTx(next.translateX);
        setTy(next.translateY);
    };

    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        setIsPanning(true);
        setPanStart({ x: e.clientX - tx, y: e.clientY - ty });
    };

    const onMouseMove = (e) => {
        if (!isPanning) return;
        setTx(e.clientX - panStart.x);
        setTy(e.clientY - panStart.y);
    };

    const onMouseUp = () => setIsPanning(false);
    const onMouseLeave = () => setIsPanning(false);

    // ============================================
    // ANNOTATIONS
    // ============================================

    /**
     * Handle viewport click to add annotation
     */
    const onViewportClick = (e) => {
        if (isPanning || draft || edit || readOnly) return;
        
        const vp = viewportRef.current;
        if (!vp) return;

        const rect = vp.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const point = calculateAnnotationPoint({
            viewportX: x,
            viewportY: y,
            scale,
            translateX: tx,
            translateY: ty,
            imageWidth: baseSize.w,
            imageHeight: baseSize.h,
        });
        const imgX = (point.xPct / 100) * baseSize.w;
        const imgY = (point.yPct / 100) * baseSize.h;

        if (imgX < 0 || imgY < 0 || imgX > baseSize.w || imgY > baseSize.h) return;

        setDraft({
            ...point,
            text: "",
            screenX: e.clientX,
            screenY: e.clientY,
        });
    };

    /**
     * Save new annotation
     */
    const saveDraft = async () => {
        if (!draft?.text?.trim() || !imageUrl) return;

        try {
            const res = await axios.post(`${API_BASE_URL}doctor-documents/annotations`, {
                doctor_id: doctorId,
                image_url: imageUrl,
                x_pct: draft.xPct,
                y_pct: draft.yPct,
                text: draft.text,
            });

            if (res.data?.success) {
                toast.success("Annotation ajoutée !");
                setAnnotations((prev) => [...prev, res.data.data]);
                setDraft(null);
            }
        } catch (err) {
            console.error("Save annotation error:", err);
            toast.error("Erreur lors de l'ajout de l'annotation");
        }
    };

    /**
     * Cancel annotation draft
     */
    const cancelDraft = () => setDraft(null);

    /**
     * Open edit popover for existing annotation
     */
    const openEditPopover = (e, annotation) => {
        e.stopPropagation();
        setEdit({
            id: annotation.id,
            text: annotation.text,
            xPct: annotation.xPct || annotation.x_pct,
            yPct: annotation.yPct || annotation.y_pct,
            screenX: e.clientX,
            screenY: e.clientY,
        });
    };

    /**
     * Update annotation
     */
    const updateAnnotation = async () => {
        if (!edit?.text?.trim()) return;

        try {
            const res = await axios.put(`${API_BASE_URL}doctor-documents/annotations/${edit.id}`, {
                doctor_id: doctorId,
                text: edit.text,
            });

            if (res.data?.success) {
                toast.success("Annotation mise à jour !");
                setAnnotations((prev) =>
                    prev.map((a) => (a.id === edit.id ? { ...a, text: edit.text } : a))
                );
                setEdit(null);
            }
        } catch (err) {
            console.error("Update annotation error:", err);
            toast.error("Erreur lors de la mise à jour");
        }
    };

    /**
     * Delete annotation
     */
    const deleteAnnotation = async () => {
        if (!edit?.id) return;

        const confirmed = window.confirm("Supprimer cette annotation ?");
        if (!confirmed) return;

        try {
            const res = await axios.delete(`${API_BASE_URL}doctor-documents/annotations/${edit.id}`, {
                params: { doctor_id: doctorId },
            });

            if (res.data?.success) {
                toast.success("Annotation supprimée !");
                setAnnotations((prev) => prev.filter((a) => a.id !== edit.id));
                setEdit(null);
            }
        } catch (err) {
            console.error("Delete annotation error:", err);
            toast.error("Erreur lors de la suppression");
        }
    };

    /**
     * Handle close
     */
    const handleClose = () => {
        setEdit(null);
        setDraft(null);
        onClose();
    };

    // ============================================
    // RENDER
    // ============================================

    if (!imageUrl) return null;

    return (
        <div
            className="ImgPreviewOverlay"
            onClick={handleClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: 16,
            }}
        >
            <div
                className="ImgPreviewBox"
                style={{
                    position: "relative",
                    background: "#fff",
                    padding: 10,
                    borderRadius: 10,
                    maxWidth: "92vw",
                    maxHeight: "92vh",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                    display: "grid",
                    gridTemplateRows: "auto 1fr",
                    gap: 8,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Annotation Controls */}
                <div
                    className="AnnotControls"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        justifyContent: "space-between",
                    }}
                >
                    {/* Zoom controls */}
                    <div className="BtnGroup">
                        <button
                            className="Btn Btn--icon"
                            onClick={zoomOut}
                            title="Zoom - (molette)"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M5 12h14"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <span className="sr-only">Zoom -</span>
                        </button>

                        <button
                            className="Btn Btn--icon"
                            onClick={zoomIn}
                            title="Zoom + (molette)"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M12 5v14M5 12h14"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <span className="sr-only">Zoom +</span>
                        </button>

                        <button
                            className="Btn"
                            onClick={resetView}
                            title="Réinitialiser la vue"
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                style={{ marginRight: 6 }}
                            >
                                <path
                                    d="M3 12a9 9 0 1 0 3-6.708V7M3 7h4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                            Reset
                        </button>
                    </div>

                    {/* Close button */}
                    <button
                        onClick={handleClose}
                        aria-label="Fermer"
                        className="Btn Btn--danger"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            style={{ marginRight: 6 }}
                        >
                            <path
                                d="M6 6l12 12M18 6L6 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                        Fermer
                    </button>
                </div>

                {/* Viewport (zoom/pan + annotations) */}
                <div
                    ref={viewportRef}
                    className="AnnotViewport"
                    onWheel={handleWheel}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseLeave}
                    onClick={onViewportClick}
                    style={{
                        position: "relative",
                        width: "86vw",
                        height: "80vh",
                        overflow: "hidden",
                        background: "#f7f7f7",
                        borderRadius: 8,
                        cursor: isPanning ? "grabbing" : "grab",
                    }}
                >
                    {/* Transformed layer */}
                    <div
                        className="AnnotLayer"
                        style={{
                            position: "absolute",
                            inset: 0,
                            transformOrigin: "0 0",
                            transform: `scale(${scale}) translate(${tx}px, ${ty}px)`,
                            width: baseSize.w,
                            height: baseSize.h,
                        }}
                    >
                        <img
                            ref={imgRef}
                            src={imageUrl}
                            alt="Preview"
                            onLoad={handleImgLoad}
                            style={{
                                display: "block",
                                maxWidth: "86vw",
                                maxHeight: "80vh",
                                borderRadius: 6,
                                objectFit: "contain",
                                pointerEvents: "none",
                                userSelect: "none",
                            }}
                        />

                        {/* Existing annotation pins */}
                        {annotations.map((a) => (
                            <div
                                key={a.id}
                                className="PinItem"
                                title={a.text}
                                style={{
                                    position: "absolute",
                                    left: `calc(${a.xPct || a.x_pct}% - 10px)`,
                                    top: `calc(${a.yPct || a.y_pct}% - 10px)`,
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    background: "#ff2d2d",
                                    boxShadow: "0 0 0 2px #fff",
                                    display: "grid",
                                    placeItems: "center",
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                                onClick={(e) => openEditPopover(e, a)}
                            >
                                ●
                            </div>
                        ))}
                    </div>
                </div>

                {/* New annotation popover */}
                {draft && (
                    <div
                        className="AnnotPopover"
                        style={{
                            position: "fixed",
                            left: draft.screenX + 12,
                            top: draft.screenY + 12,
                            background: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: 8,
                            boxShadow: "0 8px 30px rgba(0,0,0,.18)",
                            padding: 10,
                            zIndex: 1100,
                            width: 260,
                            display: "grid",
                            gap: 8,
                        }}
                    >
                        <div style={{ fontWeight: 600 }}>Nouveau commentaire</div>
                        <textarea
                            value={draft.text}
                            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                            placeholder="Écrire un commentaire…"
                            rows={3}
                            style={{ width: "100%", resize: "vertical" }}
                        />
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button className="Btn Btn--light" onClick={cancelDraft}>
                                Annuler
                            </button>
                            <button className="Btn Btn--primary" onClick={saveDraft}>
                                Enregistrer
                            </button>
                        </div>
                    </div>
                )}

                {/* Edit annotation popover */}
                {edit && (
                    <div
                        className="AnnotPopover"
                        style={{
                            position: "fixed",
                            left: edit.screenX + 12,
                            top: edit.screenY + 12,
                            background: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: 8,
                            boxShadow: "0 8px 30px rgba(0,0,0,.18)",
                            padding: 10,
                            zIndex: 1100,
                            width: 280,
                            display: "grid",
                            gap: 8,
                        }}
                    >
                        <div style={{ fontWeight: 600 }}>Commentaire</div>
                        <textarea
                            value={edit.text}
                            onChange={(e) => setEdit({ ...edit, text: e.target.value })}
                            placeholder="Modifier le commentaire…"
                            rows={3}
                            style={{ width: "100%", resize: "vertical" }}
                        />
                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                                justifyContent: "space-between",
                            }}
                        >
                            <button
                                className="Btn Btn--danger"
                                onClick={deleteAnnotation}
                            >
                                🗑️ Supprimer
                            </button>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    className="Btn Btn--light"
                                    onClick={() => setEdit(null)}
                                >
                                    Fermer
                                </button>
                                <button
                                    className="Btn Btn--primary"
                                    onClick={updateAnnotation}
                                    disabled={!edit.text || !edit.text.trim()}
                                    title={
                                        !edit.text || !edit.text.trim()
                                            ? "Le commentaire est vide"
                                            : "Mettre à jour"
                                    }
                                >
                                    Mettre à jour
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImagePreviewModal;
